<script lang="ts" setup>
import type { GlobalCollectionId } from '#layers/data-model/public';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { CrudAction, useAuthorization } from '#layers/access/public';
import { useCollection, useQueryApi } from '#layers/data-model/public';

const { collectionId } = defineProps<{
	collectionId: GlobalCollectionId;
	iconOnly?: boolean;
}>();

defineEmits<{
	click: [];
}>();

const { t } = useI18n();
const { can } = useAuthorization();
const { collection } = useCollection(() => collectionId);

// The API can: the collection's query schema exposes a create operation.
const supportsCreate = useQueryApi(collection).supports((api) => api.create() !== undefined);

// The user may: permissions on top of what the API accepts.
const canCreate = computed(() =>
	collection.value ? supportsCreate.value && can({ collection: collection.value, action: CrudAction.Create }) : false,
);

const disabledReason = computed(() =>
	supportsCreate.value ? 'permissions.no_create_access' : 'permissions.no_create_support',
);

const tooltipText = computed(() => canCreate.value ? t('common.add_item') : t(disabledReason.value));
</script>

<template>
	<DTooltip :text="tooltipText" :disabled="canCreate && !iconOnly">
		<DButton
			:label="iconOnly ? undefined : $t('common.add_item')"
			:aria-label="iconOnly ? $t('common.add_item') : undefined"
			:square="iconOnly"
			size="sm"
			icon="lucide:plus"
			:disabled="!canCreate"
			@click="$emit('click')"
		/>
	</DTooltip>
</template>
